import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { v4 as uuidv4 } from 'uuid';
import { QuotationCustomerService } from '../../services/quotation-customer.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface Invoice {
  invoiceNo: string;
  date: string;
  invoicePrice: number;
}

interface Payment {
  invoiceNo: string;
  paymentPrice: number;
  invoiceDate: string;
  paymentReceivedDate: string;
  paymentSlips?: any[];
  notes?: string; // Notes field
  paymentReceived: boolean;
}
// Add this interface at the top of your component file
interface PaymentSlipFile {
  file: File | string;
  name: string;
  size: number;
  type: string;
  id?: string;
  base64Data?: string;
  originalFileName?: string;
  originalFileSize?: number;
  originalFileType?: string;
  isFromSaved?: boolean;
}


@Component({
  selector: 'app-quotation-form-dialog',
  templateUrl: './quotation-form-dialog.component.html',
  styleUrls: ['./quotation-form-dialog.component.scss']
})
export class QuotationFormDialogComponent implements OnInit {
  forms: { [key: string]: FormGroup } = {};
  currentStep: 'quotation' | 'po' | 'pi' | 'workOrder' | 'ci' | 'payment' = 'quotation';
  steps = ['quotation', 'po', 'pi', 'workOrder', 'ci', 'payment'];
  workflowData: any = {};
  orderId: string;
  customers: any[] = []; // List of all customers for dropdown
  filteredCustomers: any[] = []; // Filtered suggestions for autocomplete
  loadingCustomers: boolean = false;
  selectedCustomerUuid: string | null = null;
  fileInput: HTMLInputElement | null = null;
  displayPdfPreview = false;
  safePdfUrl: SafeResourceUrl | null = null;
  invoiceOptions: any[] = []; // Options for invoice dropdown in payments
  stepImages: { [key: string]: string } = {
    quotation: 'assets/quotation-dashboard/quotation.png',
    po: 'assets/quotation-dashboard/po.png',
    pi: 'assets/quotation-dashboard/pi.png',
    workOrder: 'assets/quotation-dashboard/workOrder.png',
    ci: 'assets/quotation-dashboard/ci.png',
    payment: 'assets/quotation-dashboard/payment.png',
  };
  displayPaymentSlipPreview = false;
  currentPaymentSlipUrl: SafeResourceUrl | null = null;
  currentPaymentSlipType: 'pdf' | 'image' | null = null;
  paymentSlipFileInputs: { [key: number]: HTMLInputElement } = {};
  constructor(
    private fb: FormBuilder,
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private customerService: QuotationCustomerService,
    private messageService: MessageService,
    private sanitizer: DomSanitizer
  ) {
    this.orderId = uuidv4();
  }

ngOnInit(): void {
  if (this.config.data) {
    console.log('this.config.data', this.config.data)
    this.workflowData = this.config.data.workflowData || this.getDefaultWorkflowData();
    if (this.config.data.startStep && this.steps.includes(this.config.data.startStep)) {
      this.currentStep = this.config.data.startStep;
    }
    if (this.config.data.orderId) {
      this.orderId = this.config.data.orderId;
    }
  } else {
    this.workflowData = this.getDefaultWorkflowData();
  }

  this.initializeForms();

  // Ensure at least one invoice exists after forms are initialized
  if (this.getInvoicesControls().length === 0) {
    this.addInvoice();
  }

  // Load existing payments with migration support
  this.loadExistingPayments();

  this.loadCustomers();
  this.config.header = this.getStepHeader(this.currentStep);
}

loadExistingPayments(): void {
  if (this.workflowData.payments && this.workflowData.payments.length > 0) {
    // Clear existing payments first
    const paymentsArray = this.forms['payment'].get('payments') as FormArray;
    while (paymentsArray.length !== 0) {
      paymentsArray.removeAt(0);
    }
    
    // Load each existing payment with migration
    this.workflowData.payments.forEach((paymentData: any) => {
      this.addPayment(
        paymentData.invoiceNo || '',
        paymentData.paymentPrice || 0,
        paymentData.paymentReceivedDate || this.getTodayDate(),
        paymentData.paymentReceived || false,
        [], // paymentSlips will be handled by migration
        paymentData.notes || '',
        paymentData // Pass the entire payment data for migration
      );
    });
    
    // Recalculate all remainders after loading
    setTimeout(() => {
      this.updateAllRemainders();
    }, 0);
  } else {
    // Ensure at least one payment exists if no existing data
    if (this.getPaymentsControls().length === 0) {
      this.addPayment();
    }
  }
}
onInvoiceSelect(event: any, paymentIndex: number): void {
  const payments = this.forms['payment'].get('payments') as FormArray;
  const paymentGroup = payments.at(paymentIndex) as FormGroup;
  const selectedInvoiceNo = event.value;
  
  if (selectedInvoiceNo) {
    const invoice = this.workflowData.invoices?.find((inv: any) => inv.invoiceNo === selectedInvoiceNo);
    
    if (invoice) {
      // Force update the invoice price
      paymentGroup.get('invoicePrice')?.setValue(invoice.invoicePrice, { emitEvent: false });
      paymentGroup.get('invoiceDate')?.setValue(invoice.date, { emitEvent: false });
      
      // Update all remainders
      setTimeout(() => {
        this.updateAllRemainders();
      }, 0);
    }
  }
}

// Add this helper method to refresh invoice options
refreshInvoiceOptions(): void {
  if (this.workflowData.invoices && this.workflowData.invoices.length > 0) {
    this.invoiceOptions = this.workflowData.invoices.map((invoice: any) => ({
      label: `${invoice.invoiceNo} - ${this.formatCurrency(invoice.invoicePrice)}`,
      value: invoice.invoiceNo
    }));
  } else {
    this.invoiceOptions = [];
  }
}
formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
}
  loadCustomers(): void {
    this.loadingCustomers = true;
    this.customerService.getCustomers().subscribe({
      next: (customers) => {
        this.customers = customers.map(customer => ({
          label: customer.customerDetails.customerName,
          value: customer.uuid,
          data: customer
        }));
        this.loadingCustomers = false;
        if (this.workflowData.customerName) {
          this.setInitialCustomer();
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load customers',
          life: 3000
        });
        this.loadingCustomers = false;
      }
    });
  }

  filterCustomers(event: any): void {
    const query = event.query.toLowerCase();
    this.filteredCustomers = this.customers.filter(customer =>
      customer.label.toLowerCase().includes(query)
    );
  }

  setInitialCustomer(): void {
    const quotationForm = this.forms['quotation'];
    if (this.workflowData.uuid) {
      this.customerService.getCustomerByUuid(this.workflowData.uuid).subscribe({
        next: (customer) => {
          const selectedShipping = customer.shippingDetails?.find(
            (detail: any) => detail.isSelectedShipping
          ) || customer.shippingDetails?.[0] || {};

          quotationForm.patchValue({
            customer: { label: customer.customerDetails.customerName, value: customer.uuid },
            customerUuid: customer.uuid,
            customerAddress: customer.customerDetails.customerAddress || '',
            attention: customer.contactPerson.contactPersonName || '',
            email: customer.contactPerson.contactPersonEmail || '',
            country: selectedShipping.country || ''
          });

          this.updateRelatedForms(customer, selectedShipping);
          this.workflowData.email = customer.contactPerson.contactPersonEmail || '';
          this.workflowData.shippingID = selectedShipping.shippingID || '';
          this.workflowData.customerName = customer.customerDetails.customerName || '';
          this.workflowData.uuid = customer.uuid;
          this.syncFormData();
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to fetch customer details',
            life: 3000
          });
        }
      });
    } else if (this.workflowData.customerName) {
      quotationForm.patchValue({
        customer: { label: this.workflowData.customerName, value: this.workflowData.uuid },
        customerUuid: '',
        customerAddress: this.workflowData.customerAddress || '',
        attention: this.workflowData.attention || '',
        email: this.workflowData.email || '',
        country: this.workflowData.country || ''
      });
      this.workflowData.customerName = this.workflowData.customerName;
      this.syncFormData();
    }
  }
onPaymentSlipSelect(event: Event, paymentIndex: number): void {
  const input = event.target as HTMLInputElement;
  this.paymentSlipFileInputs[paymentIndex] = input;
  
  if (input.files && input.files.length > 0) {
    const file = input.files[0];
    
    // Validate file type (PDF, JPG, PNG)
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid File Type',
        detail: 'Please select a PDF, JPG, or PNG file',
        life: 3000
      });
      this.clearPaymentSlip(paymentIndex);
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10000000) {
      this.messageService.add({
        severity: 'error',
        summary: 'File Too Large',
        detail: 'File size must be less than 10MB',
        life: 3000
      });
      this.clearPaymentSlip(paymentIndex);
      return;
    }

    // Store the file in the payment form control
    const paymentsArray = this.forms['payment'].get('payments') as FormArray;
    const paymentControl = paymentsArray.at(paymentIndex);
    paymentControl.get('paymentSlip')?.setValue(file);

    console.log(`Payment slip selected for payment ${paymentIndex}:`, {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    this.messageService.add({
      severity: 'success',
      summary: 'Payment Slip Selected',
      detail: `File ${file.name} selected successfully`,
      life: 3000
    });
  } else {
    this.clearPaymentSlip(paymentIndex);
  }
}

clearPaymentSlip(paymentIndex: number): void {
  const paymentsArray = this.forms['payment'].get('payments') as FormArray;
  const paymentControl = paymentsArray.at(paymentIndex);
  
  if (paymentControl) {
    // Clear the payment slip value
    paymentControl.get('paymentSlip')?.setValue(null);
    
    // Clear file input if it exists
    if (this.paymentSlipFileInputs[paymentIndex]) {
      this.paymentSlipFileInputs[paymentIndex].value = '';
    }
    
    // Close preview dialog if open
    this.displayPaymentSlipPreview = false;
    this.currentPaymentSlipUrl = null;
    
    this.messageService.add({
      severity: 'info',
      summary: 'Payment Slip Removed',
      detail: 'Payment slip has been removed successfully',
      life: 3000
    });
  }
}

  getInvoicesControls() {
    return (this.forms['ci'].get('invoices') as FormArray).controls;
  }

  getPaymentsControls() {
    return (this.forms['payment'].get('payments') as FormArray).controls;
  }
  onCustomerSelect(event: any): void {
    const selectedCustomer = event; // The selected customer object from p-autoComplete
    this.selectedCustomerUuid = selectedCustomer.value;

    if (selectedCustomer.value) {
      this.customerService.getCustomerByUuid(selectedCustomer.value).subscribe({
        next: (customer) => {
          const selectedShipping = customer.shippingDetails?.find(
            (detail: any) => detail.isSelectedShipping
          ) || customer.shippingDetails?.[0] || {};

          const quotationForm = this.forms['quotation'];
          quotationForm.patchValue({
            customer: { label: customer.customerDetails.customerName, value: customer.uuid }, // Set as object
            customerUuid: customer.uuid,
            customerAddress: customer.customerDetails.customerAddress || '',
            attention: customer.contactPerson.contactPersonName || '',
            email: customer.contactPerson.contactPersonEmail || '',
            country: selectedShipping.country || ''
          });

          this.updateRelatedForms(customer, selectedShipping);
          this.workflowData.email = customer.contactPerson.contactPersonEmail || '';
          this.workflowData.shippingID = selectedShipping.shippingID || '';
          this.workflowData.customerName = customer.customerDetails.customerName || '';
          this.workflowData.uuid = customer.uuid;
          this.syncFormData();
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to fetch customer details',
            life: 3000
          });
        }
      });
    }
  }
  onCustomerClear(): void {
    this.selectedCustomerUuid = null;
    this.forms['quotation'].patchValue({
      customer: '', // Clear the customer field
      customerUuid: '',
      customerAddress: '',
      attention: '',
      email: '',
      country: ''
    });
    this.workflowData.uuid = '';
    this.workflowData.email = '';
    this.workflowData.shippingID = '';
    this.workflowData.customerName = '';
    this.syncFormData();
  }

  updateRelatedForms(customer: any, selectedShipping: any): void {
    const poForm = this.forms['po'];
    const piForm = this.forms['pi'];
    const workOrderForm = this.forms['workOrder'];
    const ciForm = this.forms['ci'];

    if (!poForm.get('deliveryToCompany')?.value) {
      poForm.patchValue({
        deliveryToCompany: customer.customerDetails.customerAddress || '',
        deliveryToAddress: this.formatShippingDetails(customer.shippingDetails) || ''
      });
    }

    if (!piForm.get('deliveryToCompany')?.value) {
      piForm.patchValue({
        deliveryToCompany: customer.customerDetails.customerAddress || '',
        deliveryToAddress: this.formatShippingDetails(customer.shippingDetails) || ''
      });
    }
    if (!poForm.get('customerName')?.value) {
      poForm.patchValue({
        customerName: customer.customerDetails.customerName || '',
        customerAddress: customer.customerDetails.customerAddress || ''
      });
    }
    if (!piForm.get('customerName')?.value) {
      poForm.patchValue({
        customerName: customer.customerDetails.customerName || '',
        customerAddress: customer.customerDetails.customerAddress || ''
      });
    }
    if (!workOrderForm.get('customerName')?.value) {
      workOrderForm.patchValue({
        customerName: customer.customerDetails.customerName || '',
        customerAddress: customer.customerDetails.customerAddress || ''
      });
    }

    if (!ciForm.get('customerName')?.value) {
      ciForm.patchValue({
        customerName: customer.customerDetails.customerName || '',
        customerAddress: customer.customerDetails.customerAddress || ''
      });
    }
  }
  formatShippingDetails(shippingDetails: any[]): string {
    if (!shippingDetails || shippingDetails.length === 0) {
      return '';
    }
    const detail = shippingDetails.find(detail => detail.isSelectedShipping) || shippingDetails[0];
    const fields = [
      detail.companyName,
      detail.shippingAddress,
      detail.street,
      detail.postalCode,
      detail.country,
      detail.fax
    ].filter(field => field);
    return fields.join(', ');
  }
  getFileName(): string {
    if (this.workflowData.uploadDoc instanceof File) {
      return this.workflowData.uploadDoc.name;
    }
    return '';
  }

previewPaymentSlip(paymentIndex: number, slipIndex?: number): void {
  const paymentsArray = this.forms['payment'].get('payments') as FormArray;
  const paymentControl = paymentsArray.at(paymentIndex);
  const paymentSlips: PaymentSlipFile[] = paymentControl.get('paymentSlips')?.value || [];

  let slipToPreview: PaymentSlipFile | null = null;
  
  if (slipIndex !== undefined && slipIndex >= 0 && slipIndex < paymentSlips.length) {
    slipToPreview = paymentSlips[slipIndex];
  } else if (paymentSlips.length > 0) {
    slipToPreview = paymentSlips[0];
  }

  if (!slipToPreview) {
    this.messageService.add({
      severity: 'warn',
      summary: 'No File',
      detail: 'No payment slip file is available for preview.',
      life: 3000
    });
    return;
  }

  try {
    // Handle different file sources with proper type guards
    if (slipToPreview.file instanceof File) {
      // Store the File reference after type guard
      const fileReference = slipToPreview.file; // Now TypeScript knows this is a File
      
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        this.currentPaymentSlipUrl = this.sanitizer.bypassSecurityTrustResourceUrl(result);
        // Now we can safely access .type on fileReference
        this.currentPaymentSlipType = fileReference.type === 'application/pdf' ? 'pdf' : 'image';
        this.displayPaymentSlipPreview = true;
      };
      reader.onerror = () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Preview Error',
          detail: 'Failed to read the payment slip file',
          life: 3000
        });
      };
      reader.readAsDataURL(fileReference);
    } else if (typeof slipToPreview.file === 'string' && slipToPreview.file.startsWith('data:')) {
      // Saved base64 data - use the type property from the slip object
      this.currentPaymentSlipUrl = this.sanitizer.bypassSecurityTrustResourceUrl(slipToPreview.file);
      this.currentPaymentSlipType = slipToPreview.type === 'application/pdf' ? 'pdf' : 'image';
      this.displayPaymentSlipPreview = true;
    } else if (slipToPreview.base64Data) {
      // Saved data with separate base64Data property
      this.currentPaymentSlipUrl = this.sanitizer.bypassSecurityTrustResourceUrl(slipToPreview.base64Data);
      this.currentPaymentSlipType = (slipToPreview.originalFileType || slipToPreview.type) === 'application/pdf' ? 'pdf' : 'image';
      this.displayPaymentSlipPreview = true;
    } else {
      throw new Error('Invalid file format for preview');
    }
  } catch (error) {
    console.error('Preview error:', error);
    this.messageService.add({
      severity: 'error',
      summary: 'Preview Error',
      detail: 'Failed to preview payment slip',
      life: 3000
    });
  }
}

// Download payment slip
downloadPaymentSlip(paymentIndex: number, slipIndex?: number): void {
  const paymentsArray = this.forms['payment'].get('payments') as FormArray;
  const paymentControl = paymentsArray.at(paymentIndex);
  const paymentSlips: PaymentSlipFile[] = paymentControl.get('paymentSlips')?.value || [];

  let slipToDownload: PaymentSlipFile | null = null;
  
  if (slipIndex !== undefined && slipIndex >= 0 && slipIndex < paymentSlips.length) {
    slipToDownload = paymentSlips[slipIndex];
  } else if (paymentSlips.length > 0) {
    slipToDownload = paymentSlips[0];
  }

  if (!slipToDownload) {
    this.messageService.add({
      severity: 'warn',
      summary: 'No File',
      detail: 'No payment slip file available for download.',
      life: 3000
    });
    return;
  }

  try {
    if (slipToDownload.file instanceof File) {
      const url = URL.createObjectURL(slipToDownload.file);
      const link = document.createElement('a');
      link.href = url;
      link.download = slipToDownload.file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else if (typeof slipToDownload.file === 'string' && slipToDownload.file.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = slipToDownload.file;
      link.download = slipToDownload.originalFileName || 
        `payment-slip-${paymentIndex + 1}-${slipIndex !== undefined ? slipIndex + 1 : 1}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (slipToDownload.base64Data) {
      const link = document.createElement('a');
      link.href = slipToDownload.base64Data;
      link.download = slipToDownload.originalFileName || 
        `payment-slip-${paymentIndex + 1}-${slipIndex !== undefined ? slipIndex + 1 : 1}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error('Download error:', error);
    this.messageService.add({
      severity: 'error',
      summary: 'Download Error',
      detail: 'Failed to download payment slip',
      life: 3000
    });
  }
}

// Utility methods
generateFileId(): string {
  return Math.random().toString(36).substr(2, 9);
}

getFileIcon(fileType: string): string {
  if (fileType === 'application/pdf') {
    return 'pi pi-file-pdf';
  } else if (fileType.startsWith('image/')) {
    return 'pi pi-image';
  }
  return 'pi pi-file';
}

formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
// Get payment slip file name
getPaymentSlipFileName(paymentIndex: number): string {
  const paymentsArray = this.forms['payment'].get('payments') as FormArray;
  const paymentControl = paymentsArray.at(paymentIndex);
  const paymentSlip = paymentControl.get('paymentSlip')?.value;
  
  if (paymentSlip instanceof File) {
    return paymentSlip.name;
  } else if (typeof paymentSlip === 'string' && paymentSlip.startsWith('data:')) {
    // Extract file type from base64 string
    const mimeType = paymentSlip.split(';')[0].split(':')[1];
    const extension = mimeType.includes('pdf') ? 'pdf' : 
                     mimeType.includes('jpeg') ? 'jpg' : 
                     mimeType.includes('png') ? 'png' : 'file';
    return `payment-slip-${paymentIndex + 1}.${extension}`;
  }
  return '';
}

  onPdfLoadError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Load Error',
      detail: 'Failed to load the PDF. The file may be corrupted or unsupported.',
    });
    this.safePdfUrl = null;
    this.displayPdfPreview = false;
  }

  initializeForms(): void {
    const today = this.getTodayDate();

    this.forms['quotation'] = this.fb.group({
      quotationId: [this.workflowData.quotationId || uuidv4()],
      quotationNo: [this.workflowData.quotationNo || '', Validators.required],
      customer: [this.workflowData.customerName || '', Validators.required], // Initialize as string
      customerUuid: [this.workflowData.uuid || ''],
      customerAddress: [this.workflowData.customerAddress || '', Validators.required],
      project: [this.workflowData.project || ''],
      attention: [this.workflowData.attention || ''],
      date: [this.workflowData.quotationDate || today, Validators.required],
      description: [this.workflowData.description || ''],
      totalPrice: [this.workflowData.totalPrice || 0, [Validators.required, Validators.min(0)]],
      country: [this.workflowData.country || '', Validators.required],
      email: [this.workflowData.email || '', [Validators.required, Validators.email]]
    });

    // Add valueChanges listener to sync customer name to workflowData
    this.forms['quotation'].get('customer')?.valueChanges.subscribe(value => {
      const customerValue = typeof value === 'object' && value !== null ? value.label : value;
      this.workflowData.customerName = customerValue || '';
      // If no customerUuid, assume manual entry
      if (!this.forms['quotation'].get('customerUuid')?.value) {
        this.workflowData.uuid = '';
        this.workflowData.shippingID = '';
      }
      this.syncFormData();
    });

    // Other forms remain unchanged
    this.forms['po'] = this.fb.group({
      poId: [this.workflowData.poId || uuidv4()],
      deliveryToCompany: [this.workflowData.deliveryToCompany || '', Validators.required],
      deliveryToAddress: [this.workflowData.deliveryToAddress || ''],
      poNumber: [this.workflowData.poNumber || '', Validators.required],
      customerName: [this.workflowData.customerName || '', Validators.required],
      date: [this.workflowData.poDate || today, Validators.required],
      shipDate: [this.workflowData.shipDate || today],
      remarks: [this.workflowData.remarks || ''],
      quotationNo: [{ value: this.workflowData.quotationNo || '', disabled: true }],
      totalPrice: [this.workflowData.totalPrice || 0, [Validators.required, Validators.min(0)]]
    });

    this.forms['pi'] = this.fb.group({
      piId: [this.workflowData.piId || uuidv4()],
      deliveryToCompany: [this.workflowData.deliveryToCompany || '', Validators.required],
      deliveryToAddress: [this.workflowData.deliveryToAddress || ''],
      proformaInvoice: [this.workflowData.proformaInvoice || '', Validators.required],
      date: [this.workflowData.piDate || today, Validators.required],
      poNumber: [{ value: this.workflowData.poNumber || '', disabled: true }],
      customerName: [this.workflowData.customerName || '', Validators.required],
      quotationNo: [{ value: this.workflowData.quotationNo || '', disabled: true }],
      shipDate: [this.workflowData.shipDate || today],
      termsOfShipment: [this.workflowData.termsOfShipment || ''],
      paymentTerms: [this.workflowData.paymentTerms || ''],
      description: [this.workflowData.piDescription || ''],
      totalPrice: [this.workflowData.totalPrice || 0, [Validators.required, Validators.min(0)]]
    });

    this.forms['workOrder'] = this.fb.group({
      piId: [{ value: this.workflowData.piId || '', disabled: true }],
      workOrderId: [this.workflowData.workOrderId || uuidv4()],
      jobNumber: [this.workflowData.jobNumber || '', Validators.required],
      customerName: [this.workflowData.customerName || '', Validators.required],
      customerAddress: [this.workflowData.customerAddress || '', Validators.required],
      projectName: [this.workflowData.project || '',],
      quotationNo: [{ value: this.workflowData.quotationNo || '', disabled: true }],
      poNumber: [{ value: this.workflowData.poNumber || '', disabled: true }],
      shipDate: [this.workflowData.shipDate || today],
      proformaInvoice: [{ value: this.workflowData.proformaInvoice || '', disabled: true }],
      poReceivedDate: [this.workflowData.poReceivedDate || today, Validators.required],
      deliveryTerms: [this.workflowData.deliveryTerms || ''],
      officiallySubmittedDate: [this.workflowData.officiallySubmittedDate || today],
      reviseNumber: [this.workflowData.reviseNumber || ''],
      officialSubmitDate: [this.workflowData.officialSubmitDate || today],
      totalPrice: [this.workflowData.totalPrice || 0, [Validators.required, Validators.min(0)]]
    });

    this.forms['ci'] = this.fb.group({
      ciId: [this.workflowData.ciId || uuidv4()],
      quotationNo: [{ value: this.workflowData.quotationNo || '', disabled: true }],
      poNumber: [{ value: this.workflowData.poNumber || '', disabled: true }],
      proformaInvoice: [{ value: this.workflowData.proformaInvoice || '', disabled: true }],
      jobNumber: [this.workflowData.jobNumber || '', Validators.required],
      customerName: [this.workflowData.customerName || '', Validators.required],
      customerAddress: [this.workflowData.customerAddress || '', Validators.required],
      shipDate: [this.workflowData.shipDate || today],
      totalPrice: [this.workflowData.totalPrice || 0, [Validators.required, Validators.min(0)]],
      invoices: this.fb.array([])  // FormArray for multiple invoices
    });

    // Initialize invoices FormArray if data exists
    (this.workflowData.invoices || []).forEach((invoice: Invoice) => {
      this.addInvoice(invoice.invoiceNo, invoice.date, invoice.invoicePrice);
    });

    this.forms['payment'] = this.fb.group({
      paymentId: [this.workflowData.paymentId || uuidv4()],
      quotationNo: [{ value: this.workflowData.quotationNo || '', disabled: true }],
      proformaInvoice: [{ value: this.workflowData.proformaInvoice || '', disabled: true }],
      poNumber: [{ value: this.workflowData.poNumber || '', disabled: true }],
      jobNumber: [{ value: this.workflowData.jobNumber || '', disabled: true }],
      customerName: [this.workflowData.customerName || '', Validators.required],
      customerAddress: [this.workflowData.customerAddress || '', Validators.required],
      totalPrice: [this.workflowData.totalPrice || 0, [Validators.required, Validators.min(0)]],
      payments: this.fb.array([])  // FormArray for multiple payments
    });

    // Initialize payments FormArray if data exists
// Initialize payments FormArray if data exists
(this.workflowData.payments || []).forEach((payment: Payment) => {
  this.addPayment(payment.invoiceNo, payment.paymentPrice, payment.paymentReceivedDate, payment.paymentReceived, payment.paymentSlips,payment.notes || '');
});


    this.syncFormData();
    this.initializeExistingPaymentSubscriptions();
  }

  // Helper methods for adding/removing invoices and payments
  addInvoice(invoiceNo = '', date = this.getTodayDate(), invoicePrice = 0) {
    const invoices = this.forms['ci'].get('invoices') as FormArray;
    invoices.push(this.fb.group({
      invoiceNo: [invoiceNo, Validators.required],
      date: [date, Validators.required],
      invoicePrice: [invoicePrice, [Validators.required, Validators.min(0)]]
    }));

    // Update invoice options for payment dropdown
    this.updateInvoiceOptions();
  }

  removeInvoice(index: number) {
    const invoices = this.forms['ci'].get('invoices') as FormArray;
    if (invoices.length > 1) {
      invoices.removeAt(index);
      this.updateInvoiceOptions();
    }
  }



addPayment(invoiceNo = '', paymentPrice = 0, paymentReceivedDate = this.getTodayDate(), 
          paymentReceived = false, paymentSlips: any[] = [], notes = '', existingPaymentData: any = null) {
  const payments = this.forms['payment'].get('payments') as FormArray;
  
  // Handle migration from old format if existingPaymentData is provided
  let migratedPaymentSlips: PaymentSlipFile[] = [];
  if (existingPaymentData) {
    migratedPaymentSlips = this.migratePaymentSlipData(existingPaymentData);
  } else {
    migratedPaymentSlips = paymentSlips || [];
  }
  
  // Find the invoice price for the initial invoiceNo
  const selectedInvoice = this.workflowData.invoices?.find((inv: any) => inv.invoiceNo === invoiceNo);
  const initialInvoicePrice = selectedInvoice ? selectedInvoice.invoicePrice : 0;
  const initialInvoiceDate = selectedInvoice ? selectedInvoice.date : '';
  
  const paymentGroup = this.fb.group({
    invoiceNo: [invoiceNo, Validators.required],
    paymentPrice: [paymentPrice, [Validators.required, Validators.min(0)]],
    paymentReceivedDate: [paymentReceivedDate, Validators.required],
    paymentReceived: [paymentReceived, Validators.required],
    invoicePrice: [{ value: initialInvoicePrice, disabled: true }],
    remainderPrice: [{ value: 0, disabled: true }],
    paymentSlips: [migratedPaymentSlips],
    notes: [notes],
    invoiceDate: [{ value: initialInvoiceDate, disabled: true }],
  });

  // Subscribe to invoiceNo changes to update invoicePrice, invoiceDate and remainder
  paymentGroup.get('invoiceNo')?.valueChanges.subscribe(selectedInvoiceNo => {
    if (selectedInvoiceNo) {
      const invoice = this.workflowData.invoices?.find((inv: any) => inv.invoiceNo === selectedInvoiceNo);
      
      if (invoice) {
        // Update invoice price and date
        paymentGroup.get('invoicePrice')?.setValue(invoice.invoicePrice, { emitEvent: false });
        paymentGroup.get('invoiceDate')?.setValue(invoice.date, { emitEvent: false });
        
        // Reset payment price to 0 when invoice changes
        paymentGroup.get('paymentPrice')?.setValue(0, { emitEvent: true });
        
        // Update all remainders
        this.updateAllRemainders();
      } else {
        // If no invoice found, reset values
        paymentGroup.get('invoicePrice')?.setValue(0, { emitEvent: false });
        paymentGroup.get('invoiceDate')?.setValue('', { emitEvent: false });
        paymentGroup.get('paymentPrice')?.setValue(0, { emitEvent: true });
      }
    } else {
      // If invoice cleared, reset all values
      paymentGroup.get('invoicePrice')?.setValue(0, { emitEvent: false });
      paymentGroup.get('invoiceDate')?.setValue('', { emitEvent: false });
      paymentGroup.get('paymentPrice')?.setValue(0, { emitEvent: true });
    }
  });

  // Subscribe to paymentPrice changes to update all remainders
  paymentGroup.get('paymentPrice')?.valueChanges.subscribe(() => {
    this.updateAllRemainders();
  });

  payments.push(paymentGroup);
  
  // Calculate initial remainder
  this.updateAllRemainders();
}
// Handle multiple payment slips selection
onMultiplePaymentSlipsSelect(event: Event, paymentIndex: number): void {
  const input = event.target as HTMLInputElement;
  
  if (input.files && input.files.length > 0) {
    const newFiles: File[] = Array.from(input.files);
    const validFiles: PaymentSlipFile[] = [];
    const maxFileSize = 10000000; // 10MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

    // Validate each file
    for (const file of newFiles) {
      if (!allowedTypes.includes(file.type)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Invalid File Type',
          detail: `File "${file.name}" is not supported. Please select PDF, JPG, or PNG files only.`,
          life: 3000
        });
        continue;
      }

      if (file.size > maxFileSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'File Too Large',
          detail: `File "${file.name}" is too large. Maximum size is 10MB.`,
          life: 3000
        });
        continue;
      }

      validFiles.push({
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        id: this.generateFileId()
      });
    }

    if (validFiles.length > 0) {
      // Get current payment slips and add new ones
      const paymentsArray = this.forms['payment'].get('payments') as FormArray;
      const paymentControl = paymentsArray.at(paymentIndex);
      const currentSlips: PaymentSlipFile[] = paymentControl.get('paymentSlips')?.value || [];
      
      const updatedSlips: PaymentSlipFile[] = [...currentSlips, ...validFiles];
      paymentControl.get('paymentSlips')?.setValue(updatedSlips);

      this.messageService.add({
        severity: 'success',
        summary: 'Files Added',
        detail: `${validFiles.length} file(s) added successfully`,
        life: 3000
      });
    }

    // Clear the input
    input.value = '';
  }
}
// Update the getPaymentSlips method to guarantee non-undefined return
getPaymentSlips(paymentIndex: number): PaymentSlipFile[] {
  try {
    const paymentsArray = this.forms['payment']?.get('payments') as FormArray;
    if (!paymentsArray || paymentIndex < 0 || paymentIndex >= paymentsArray.length) {
      return [];
    }
    
    const paymentControl = paymentsArray.at(paymentIndex);
    const slips = paymentControl?.get('paymentSlips')?.value;
    return Array.isArray(slips) ? slips : [];
  } catch (error) {
    console.error('Error getting payment slips:', error);
    return [];
  }
}

// Add a helper method for checking if slips exist
hasPaymentSlips(paymentIndex: number): boolean {
  const slips: PaymentSlipFile[] = this.getPaymentSlips(paymentIndex);
  return slips.length > 0;
}


removePaymentSlip(paymentIndex: number, slipIndex: number): void {
  const paymentsArray = this.forms['payment']?.get('payments') as FormArray;
  if (!paymentsArray || paymentIndex < 0 || paymentIndex >= paymentsArray.length) {
    return;
  }

  const paymentControl = paymentsArray.at(paymentIndex);
  const currentSlips: PaymentSlipFile[] = this.getPaymentSlips(paymentIndex);
  
  if (slipIndex < 0 || slipIndex >= currentSlips.length) {
    return;
  }
  
  const updatedSlips = currentSlips.filter((_, index) => index !== slipIndex);
  paymentControl?.get('paymentSlips')?.setValue(updatedSlips);

  this.displayPaymentSlipPreview = false;
  this.currentPaymentSlipUrl = null;

  this.messageService.add({
    severity: 'info',
    summary: 'File Removed',
    detail: 'Payment slip has been removed successfully',
    life: 3000
  });
}

// Update this method to handle the remainder calculation properly
updateAllRemainders(): void {
  const payments = this.forms['payment'].get('payments') as FormArray;
  
  // Group payments by invoice number
  const paymentsByInvoice: { [key: string]: number } = {};
  
  payments.controls.forEach((control) => {
    const invoiceNo = control.get('invoiceNo')?.value;
    const paymentPrice = Number(control.get('paymentPrice')?.value) || 0;
    
    if (invoiceNo) {
      if (!paymentsByInvoice[invoiceNo]) {
        paymentsByInvoice[invoiceNo] = 0;
      }
      paymentsByInvoice[invoiceNo] += paymentPrice;
    }
  });
  
  // Update remainder for each payment
  payments.controls.forEach((control) => {
    const invoiceNo = control.get('invoiceNo')?.value;
    const invoicePrice = Number(control.get('invoicePrice')?.value) || 0;
    
    if (invoiceNo && invoicePrice > 0) {
      const totalPaid = paymentsByInvoice[invoiceNo] || 0;
      const remainder = invoicePrice - totalPaid;
      control.get('remainderPrice')?.setValue(remainder, { emitEvent: false });
    } else {
      control.get('remainderPrice')?.setValue(0, { emitEvent: false });
    }
  });
}


  // Call this method when initializing existing payments
  initializeExistingPaymentSubscriptions() {
    const paymentsArray = this.forms['payment'].get('payments') as FormArray;

    paymentsArray.controls.forEach((paymentControl) => {
      // Subscribe to changes for existing controls
      paymentControl.get('invoiceNo')?.valueChanges.subscribe(() => {
        this.updateAllRemainders();
      });

      paymentControl.get('paymentPrice')?.valueChanges.subscribe(() => {
        this.updateAllRemainders();
      });
    });

    // Initial calculation
    this.updateAllRemainders();
  }

  // Helper method to compute remainder
  updateRemainder(paymentGroup: FormGroup) {
    const invoicePrice = paymentGroup.get('invoicePrice')?.value || 0;
    const paymentPrice = paymentGroup.get('paymentPrice')?.value || 0;
    const remainder = invoicePrice - paymentPrice;
    paymentGroup.get('remainderPrice')?.setValue(remainder);
  }
removePayment(index: number) {
  const payments = this.forms['payment'].get('payments') as FormArray;
  if (payments.length > 1) {
    // Clean up file input reference
    delete this.paymentSlipFileInputs[index];
    
    // Reindex file inputs
    const newFileInputs: { [key: number]: HTMLInputElement } = {};
    Object.keys(this.paymentSlipFileInputs).forEach(key => {
      const numKey = parseInt(key);
      if (numKey > index) {
        newFileInputs[numKey - 1] = this.paymentSlipFileInputs[numKey];
      } else if (numKey < index) {
        newFileInputs[numKey] = this.paymentSlipFileInputs[numKey];
      }
    });
    this.paymentSlipFileInputs = newFileInputs;
    
    payments.removeAt(index);
    this.updateAllRemainders();
  }
}

  // Update invoice options for payment dropdown
  updateInvoiceOptions() {
    const invoices = this.forms['ci']?.get('invoices') as FormArray;
    if (invoices) {
      this.invoiceOptions = invoices.controls.map((control, index) => ({
        label: control.get('invoiceNo')?.value || `Invoice ${index + 1}`,
        value: control.get('invoiceNo')?.value || `INV-${index + 1}`
      })).filter(option => option.value);
    }
  }
  // Update syncFormData method to handle arrays
  syncFormData(): void {
    const quotationForm = this.forms['quotation'];
    const poForm = this.forms['po'];
    const piForm = this.forms['pi'];
    const workOrderForm = this.forms['workOrder'];
    const ciForm = this.forms['ci'];
    const paymentForm = this.forms['payment'];

    if (quotationForm?.valid) {
      const quotationValue = quotationForm.getRawValue();
      const customerName = typeof quotationValue.customer === 'object' && quotationValue.customer !== null ? quotationValue.customer.label : quotationValue.customer;

      // Update other forms with quotation data
      poForm?.get('quotationNo')?.setValue(quotationValue.quotationNo);
      piForm?.get('quotationNo')?.setValue(quotationValue.quotationNo);
      workOrderForm?.get('quotationNo')?.setValue(quotationValue.quotationNo);
      ciForm?.get('quotationNo')?.setValue(quotationValue.quotationNo);
      paymentForm?.get('quotationNo')?.setValue(quotationValue.quotationNo);

      // Update customer info across forms
      poForm?.get('customerName')?.setValue(customerName);
      piForm?.get('customerName')?.setValue(customerName);
      workOrderForm?.get('customerName')?.setValue(customerName);
      ciForm?.get('customerName')?.setValue(customerName);
      paymentForm?.get('customerName')?.setValue(customerName);

      workOrderForm?.get('customerAddress')?.setValue(quotationValue.customerAddress);
      ciForm?.get('customerAddress')?.setValue(quotationValue.customerAddress);
      paymentForm?.get('customerAddress')?.setValue(quotationValue.customerAddress);

      workOrderForm?.get('projectName')?.setValue(quotationValue.project);

      // Update total price across forms
      poForm?.get('totalPrice')?.setValue(quotationValue.totalPrice);
      piForm?.get('totalPrice')?.setValue(quotationValue.totalPrice);
      workOrderForm?.get('totalPrice')?.setValue(quotationValue.totalPrice);
      ciForm?.get('totalPrice')?.setValue(quotationValue.totalPrice);
      paymentForm?.get('totalPrice')?.setValue(quotationValue.totalPrice);
    }

    if (poForm?.valid) {
      const poValue = poForm.getRawValue();
      piForm?.get('deliveryToCompany')?.setValue(poValue.deliveryToCompany);
      piForm?.get('deliveryToAddress')?.setValue(poValue.deliveryToAddress);
      piForm?.get('shipDate')?.setValue(poValue.shipDate);
      piForm?.get('poNumber')?.setValue(poValue.poNumber);

      workOrderForm?.get('shipDate')?.setValue(poValue.shipDate);
      workOrderForm?.get('poNumber')?.setValue(poValue.poNumber);

      ciForm?.get('shipDate')?.setValue(poValue.shipDate);
      ciForm?.get('poNumber')?.setValue(poValue.poNumber);

      paymentForm?.get('poNumber')?.setValue(poValue.poNumber);
    }

    if (piForm?.valid) {
      const piValue = piForm.getRawValue();
      workOrderForm?.get('proformaInvoice')?.setValue(piValue.proformaInvoice);
      ciForm?.get('proformaInvoice')?.setValue(piValue.proformaInvoice);
      paymentForm?.get('proformaInvoice')?.setValue(piValue.proformaInvoice);
    }

    if (workOrderForm?.valid) {
      const workOrderValue = workOrderForm.getRawValue();
      ciForm?.get('jobNumber')?.setValue(workOrderValue.jobNumber);
      paymentForm?.get('jobNumber')?.setValue(workOrderValue.jobNumber);
    }

    // Update invoice options when CI form changes
    if (ciForm?.valid) {
      this.updateInvoiceOptions();
    }
  }
  onPrevious(): void {
    this.updateWorkflowData();
    this.syncFormData();
    const currentIndex = this.steps.indexOf(this.currentStep);
    if (currentIndex > 0) {
      this.currentStep = this.steps[currentIndex - 1] as any;
    }
    this.config.header = this.getStepHeader(this.currentStep)
  }

  onNext(): void {
    this.updateWorkflowData();
    this.syncFormData();
    const currentIndex = this.steps.indexOf(this.currentStep);
    if (currentIndex < this.steps.length - 1) {
      this.currentStep = this.steps[currentIndex + 1] as any;
    } else {
      this.completeWorkflow();
    }
    this.config.header = this.getStepHeader(this.currentStep)
  }

  onSave(): void {
    if (this.forms[this.currentStep].valid) {
      this.updateWorkflowData();
      const quotationForm = this.forms['quotation'];
      const customerValue = quotationForm.get('customer')?.value;
      const customerName = typeof customerValue === 'object' && customerValue !== null ? customerValue.label : customerValue;
      const customerUuid = quotationForm.get('customerUuid')?.value;

      if (customerUuid) {
        // Existing customer: Save with customerUuid
        this.workflowData.uuid = customerUuid;
        this.workflowData.customerName = customerName;
      } else {
        // New customer: Save with manual customer details
        this.workflowData.uuid = ''; // Ensure no UUID for new customer
        this.workflowData.customerName = customerName;
        this.workflowData.shippingID = ''; // Clear shippingID for new customer
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: `${this.currentStep.toUpperCase()} data saved successfully`
      });

      const defaultWorkflowData = this.getDefaultWorkflowData();
      const currentFormData = this.forms[this.currentStep].getRawValue();
      Object.assign(defaultWorkflowData, this.workflowData, currentFormData);

      this.dialogRef.close({
        completed: false,
        data: defaultWorkflowData,
        orderId: this.orderId,
        currentStep: this.currentStep,
        stepIds: {
          quotationId: this.forms['quotation'].getRawValue().quotationId,
          poId: this.forms['po'].getRawValue().poId,
          piId: this.forms['pi'].getRawValue().piId,
          workOrderId: this.forms['workOrder'].getRawValue().workOrderId,
          ciId: this.forms['ci'].getRawValue().ciId,
          paymentId: this.forms['payment'].getRawValue().paymentId
        },

      });
      console.log('this.currentStep', this.currentStep)
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please complete all required fields before saving'
      });
      this.forms[this.currentStep].markAllAsTouched();
    }
  }
  onClose(): void {
    this.dialogRef.close();
  }

  // Update the updateWorkflowData method to handle arrays
  updateWorkflowData(): void {
    const formData = this.forms[this.currentStep].getRawValue();
    Object.assign(this.workflowData, formData);

    if (this.currentStep === 'quotation') {
      this.workflowData.quotationId = formData.quotationId;
      this.workflowData.quotationNo = formData.quotationNo;
      this.workflowData.uuid = formData.customerUuid;
      this.workflowData.customerName = typeof formData.customer === 'object' && formData.customer !== null ? formData.customer.label : formData.customer;
      this.workflowData.customerAddress = formData.customerAddress;
      this.workflowData.project = formData.project;
      this.workflowData.attention = formData.attention;
      this.workflowData.email = formData.email;
      this.workflowData.quotationDate = formData.date;
      this.workflowData.description = formData.description;
      this.workflowData.totalPrice = formData.totalPrice;
      this.workflowData.country = formData.country;
    } else if (this.currentStep === 'po') {
      this.workflowData.poDate = formData.date;
      this.workflowData.poId = formData.poId;
      this.workflowData.customerName = formData.customerName;
      this.workflowData.poNumber = formData.poNumber;
      this.workflowData.deliveryToCompany = formData.deliveryToCompany;
      this.workflowData.deliveryToAddress = formData.deliveryToAddress;
      this.workflowData.shipDate = formData.shipDate;
      this.workflowData.remarks = formData.remarks;
    } else if (this.currentStep === 'pi') {
      this.workflowData.piDate = formData.date;
      this.workflowData.customerName = formData.customerName;
      this.workflowData.shipDate = formData.shipDate;
      this.workflowData.piDescription = formData.description;
      this.workflowData.piId = formData.piId;
      this.workflowData.proformaInvoice = formData.proformaInvoice;
      this.workflowData.termsOfShipment = formData.termsOfShipment;
      this.workflowData.paymentTerms = formData.paymentTerms;
    } else if (this.currentStep === 'ci') {
      this.workflowData.ciId = formData.ciId;
      this.workflowData.invoices = formData.invoices; // Store the invoices array
      this.workflowData.customerName = formData.customerName;
      this.workflowData.customerAddress = formData.customerAddress;
    } else if (this.currentStep === 'workOrder') {
      this.workflowData.workOrderId = formData.workOrderId;
      this.workflowData.jobNumber = formData.jobNumber;
      this.workflowData.poReceivedDate = formData.poReceivedDate;
      this.workflowData.deliveryTerms = formData.deliveryTerms;
      this.workflowData.officiallySubmittedDate = formData.officiallySubmittedDate;
      this.workflowData.reviseNumber = formData.reviseNumber;
      this.workflowData.officialSubmitDate = formData.officialSubmitDate;
    } else if (this.currentStep === 'payment') {
      this.workflowData.paymentId = formData.paymentId;
      this.workflowData.payments = formData.payments; // Store the payments array
      this.workflowData.customerName = formData.customerName;
      this.workflowData.customerAddress = formData.customerAddress;
    }
  }

  // Update getDefaultWorkflowData to include arrays
  getDefaultWorkflowData(): any {
    return {
      quotationId: uuidv4(),
      quotationNo: '',
      uuid: '',
      customerName: '',
      customerAddress: '',
      project: '',
      attention: '',
      quotationDate: '',
      description: '',
      totalPrice: 0,
      poId: uuidv4(),
      deliveryToCompany: '',
      deliveryToAddress: '',
      poNumber: '',
      poDate: '',
      shipDate: '',
      remarks: '',
      piId: uuidv4(),
      proformaInvoice: '',
      piDate: '',
      piDescription: '',
      termsOfShipment: '',
      paymentTerms: '',
      workOrderId: uuidv4(),
      jobNumber: '',
      projectName: '',
      poReceivedDate: '',
      deliveryTerms: '',
      officiallySubmittedDate: '',
      reviseNumber: '',
      email: '',
      officialSubmitDate: '',
      ciId: uuidv4(),
      invoices: [], // Array for multiple invoices
      paymentId: uuidv4(),
      payments: [], // Array for multiple payments
      uploadDoc: null,
      country: ''
    };
  }

  isIdField(key: string): boolean {
    return [
      'quotationId',
      'poId',
      'piId',
      'workOrderId',
      'ciId',
      'paymentId'
    ].includes(key);
  }

  shouldClearField(key: string, step: string): boolean {
    const stepFields: { [key: string]: string[] } = {
      quotation: [
        'quotationNo', 'customerName', 'customerAddress', 'project',
        'attention', 'date', 'description', 'totalPrice', 'country', 'email'
      ],
      po: [
        'deliveryToCompany', 'deliveryToAddress', 'poNumber', 'poDate',
        'shipDate', 'remarks', 'totalPrice', 'customerName'
      ],
      pi: [
        'deliveryToCompany', 'deliveryToAddress', 'proformaInvoice', 'piDate',
        'poNumber', 'termsOfShipment', 'paymentTerms', 'piDescription', 'totalPrice', 'customerName', 'shipDate'
      ],
      workOrder: [
        'jobNumber', 'customerName', 'customerAddress', 'projectName',
        'poReceivedDate', 'deliveryTerms', 'officiallySubmittedDate',
        'reviseNumber', 'officialSubmitDate', 'totalPrice'
      ],
      ci: [
        'invoiceNo', 'jobNumber', 'customerName', 'customerAddress', 'ciDate', 'totalPrice'
      ],
      payment: [
        'poNumber', 'totalPrice', 'paymentReceived', 'paymentReceivedDate', 'uploadDoc', 'paymentDate', 'paymentPrice'
      ]
    };

    const currentIndex = this.steps.indexOf(this.currentStep);
    for (let i = currentIndex + 1; i < this.steps.length; i++) {
      if (stepFields[this.steps[i]].includes(key)) {
        return true;
      }
    }
    return false;
  }

  getIdFieldForStep(step: string): string {
    switch (step) {
      case 'quotation': return 'quotationId';
      case 'po': return 'poId';
      case 'pi': return 'piId';
      case 'workOrder': return 'workOrderId';
      case 'ci': return 'ciId';
      case 'payment': return 'paymentId';
      default: return '';
    }
  }

  completeWorkflow(): void {
    this.updateWorkflowData();
    this.dialogRef.close({
      completed: true,
      data: this.workflowData,
      orderId: this.orderId,
      stepIds: {
        quotationId: this.forms['quotation'].getRawValue().quotationId,
        poId: this.forms['po'].getRawValue().poId,
        piId: this.forms['pi'].getRawValue().piId,
        workOrderId: this.forms['workOrder'].getRawValue().workOrderId,
        ciId: this.forms['ci'].getRawValue().ciId,
        paymentId: this.forms['payment'].getRawValue().paymentId
      },
      currentStep: this.currentStep
    });
    this.messageService.add({
      severity: 'success',
      summary: 'Workflow Completed',
      detail: 'All steps have been completed successfully'
    });
  }

  private getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  isInvalidAndTouched(formName: string, controlName: string): boolean {
    const control = this.forms[formName]?.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getStepImage(step: string): string {
    return this.stepImages[step] || this.stepImages['default'];
  }
  getStepHeader(step: string): string {
    const headerMap: { [key: string]: string } = {
      quotation: 'CREATE QUOTATION ',
      po: 'CREATE PURCHASE ORDER',
      pi: 'CREATE PROFORMA INVOICE',
      workOrder: 'CREATE WORK ORDER',
      ci: 'CREATE COMMERCIAL INVOICE',
      payment: 'CREATE PAYMENT'
    };
    return headerMap[step] || (step.charAt(0).toUpperCase() + step.slice(1));
  }
// Helper method to migrate old paymentSlip to new paymentSlips array format
migratePaymentSlipData(paymentData: any): PaymentSlipFile[] {
  // If paymentSlips array already exists, return it
  if (paymentData.paymentSlips && Array.isArray(paymentData.paymentSlips)) {
    return paymentData.paymentSlips;
  }
  
  // If old paymentSlip exists (base64 string), convert it to new format
  if (paymentData.paymentSlip && typeof paymentData.paymentSlip === 'string' && 
      paymentData.paymentSlip.startsWith('data:')) {
    
    // Extract MIME type and filename from base64 string
    const mimeTypeMatch = paymentData.paymentSlip.match(/data:([^;]+);/);
    const mimeType = mimeTypeMatch ? mimeTypeMatch[1] : 'application/octet-stream';
    
    // Generate filename based on MIME type
    const extension = this.getFileExtensionFromMimeType(mimeType);
    const fileName = `payment-slip-${paymentData.invoiceNo || 'unknown'}.${extension}`;
    
    // Estimate file size from base64 string (approximate)
    const base64Data = paymentData.paymentSlip.split(',')[1] || '';
    const estimatedSize = Math.round((base64Data.length * 3) / 4);
    
    return [{
      id: this.generateFileId(),
      file: paymentData.paymentSlip, // Store the base64 string
      name: fileName,
      size: estimatedSize,
      type: mimeType,
      base64Data: paymentData.paymentSlip,
      originalFileName: fileName,
      originalFileSize: estimatedSize,
      originalFileType: mimeType,
      isFromSaved: true
    }];
  }
  
  return []; // Return empty array if no payment slip data exists
}

// Helper to get file extension from MIME type
getFileExtensionFromMimeType(mimeType: string): string {
  const mimeToExtension: { [key: string]: string } = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg', 
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp'
  };
  
  return mimeToExtension[mimeType] || 'file';
}


}